import React, { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, Play, Trash2, Edit2 } from "lucide-react";
import {
  getDemoVideosAPI,
  createDemoVideoAPI,
  updateDemoVideoAPI,
  deleteDemoVideoAPI,
} from "../services/apicollection";
import DemoVideoForm from "../Components/DemoVideoForm";

const DemoVideoManagementPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const controller = new AbortController();
  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDemoVideosAPI({ signal: controller.signal });
      const payload = res?.data;
      if (res?.status && payload?.demoVideos) {
        setVideos(payload.demoVideos);
      } else {
        setVideos([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load demo videos");
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
    return () => {
      controller.abort();
    };
  }, [fetchVideos]);

  const handleSave = () => {
    setShowForm(false);
    setEditingVideo(null);
    fetchVideos();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingVideo(null);
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this demo video?"))
      return;
    try {
      const res = await deleteDemoVideoAPI(id);
      if (res?.status) {
        fetchVideos();
      } else {
        alert(res?.message || "Failed to delete");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete");
    }
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <DemoVideoForm onSuccess={handleSave} onCancel={handleCancel} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Demo Videos</h2>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
        >
          <Plus size={16} />
          Add Demo Video
        </button>
      </div>

      <section>
        <h3 className="mb-3 text-xs font-medium  first-letter:uppercase tracking-wide text-slate-500">
          Videos shown on About page
        </h3>
        {loading ? (
          <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
            <Loader2 size={28} className="animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-center text-sm text-red-700">
            {error}
          </div>
        ) : videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12 text-center">
            <Play size={36} className="text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No demo videos yet</p>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="mt-2 text-sm font-medium text-slate-200 underline hover:no-underline"
            >
              Add your first demo video
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {videos.map((video) => (
              <article
                key={video._id}
                className="flex flex-col overflow-hidden rounded-lg border border-[#1d3350] bg-[#0f1c2e] shadow-sm transition-shadow hover:shadow"
              >
                <div className="relative aspect-video w-full bg-[#13233a]">
                  <iframe
                    src={video.iframeUrl}
                    title={video.title || "Demo Video"}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="h-full w-full"
                  />
                </div>
                <div className="flex min-h-0 flex-1 flex-col p-3">
                  <h4 className="truncate text-sm font-semibold text-slate-100">
                    {video.title || "Demo Video"}
                  </h4>
                  {video.description && (
                    <p className="mt-0.5 line-clamp-2 text-xs text-slate-400">
                      {video.description}
                    </p>
                  )}
                  {video.topics?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {video.topics.slice(0, 4).map((t, i) => (
                        <span
                          key={i}
                          className="rounded bg-cyan-100 px-1.5 py-0.5 text-[10px] text-cyan-800"
                        >
                          {t}
                        </span>
                      ))}
                      {video.topics.length > 4 && (
                        <span className="text-[10px] text-slate-400">
                          +{video.topics.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="mt-auto flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(video)}
                      className="inline-flex items-center gap-1 rounded border border-[#1d3350] px-2 py-1 text-xs text-slate-400 hover:bg-[#0d1b2e]"
                    >
                      <Edit2 size={12} />
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(video._id)}
                      className="inline-flex items-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={12} />
                      Delete
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {editingVideo && (
        <DemoVideoEditForm
          video={editingVideo}
          onSuccess={handleSave}
          onCancel={() => setEditingVideo(null)}
        />
      )}
    </div>
  );
};

const DemoVideoEditForm = ({ video, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    title: video?.title || "",
    iframeUrl: video?.iframeUrl || "",
    topicsText: (video?.topics || []).join("\n"),
    description: video?.description || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const parseTopics = (text) => {
    if (!text?.trim()) return [];
    return text
      .split(/[\n,]+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.iframeUrl?.trim()) {
      alert("YouTube iframe URL is required");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: formData.title?.trim() || "Demo Video",
        iframeUrl: formData.iframeUrl.trim(),
        topics: parseTopics(formData.topicsText),
        description: formData.description?.trim() || "",
      };
      const res = await updateDemoVideoAPI(video._id, payload);
      if (!res?.status) {
        alert(res?.message || "Failed to update demo video");
      } else if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      alert(
        error.response?.data?.message ||
          error.message ||
          "Failed to update demo video"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="mx-auto max-w-2xl w-full rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4 shadow-lg sm:p-6">
        <h3 className="mb-4 text-base font-semibold text-slate-200">
          Edit Demo Video
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
              placeholder="Demo Video"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              YouTube iframe URL *
            </label>
            <textarea
              value={formData.iframeUrl}
              onChange={(e) => handleChange("iframeUrl", e.target.value)}
              rows={2}
              required
              className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Topics covered (one per line or comma-separated)
            </label>
            <textarea
              value={formData.topicsText}
              onChange={(e) => handleChange("topicsText", e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border border-[#1d3350] px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-[#1d3350] px-3 py-1.5 text-sm text-slate-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DemoVideoManagementPage;
